import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import ReactTooltip from 'react-tooltip';
import styled from 'styled-components';
import '../enviro-deep-research/index.scss';
import { permitAgentChat } from '../../services/firebase';

const ACCENT = '#34d399';
const TEXT_GRAY = '#94a3b8';
const BG_DARK = '#000000';
// tslint:disable-next-line:quotemark
const SIDEBAR_FONT = "var(--sans, 'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif)";

const StyledTooltip = styled(ReactTooltip)`
  max-width: 200px !important;
`;

const PermitAgentSidebar = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 190px;
  height: 100vh;
  overflow-y: auto;
  border-right: 1px solid rgba(130, 173, 181, 0.24);
  z-index: 1039;
  background-color: ${BG_DARK};
  box-shadow: none;
  font-family: ${SIDEBAR_FONT};
`;

const SidebarHeader = styled.div`
  padding: 8px 10px;
  border-bottom: 1px solid rgba(130, 173, 181, 0.2);
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const HeaderIcon = styled.i`
  font-size: 16px;
  color: #78929a;
  cursor: pointer;
  padding: 6px;
  &:hover {
    color: ${ACCENT};
  }
`;

const TabRow = styled.div`
  display: flex;
  border-bottom: 1px solid rgba(130, 173, 181, 0.2);
`;

const TabButton = styled.button<{ active: boolean }>`
  flex: 1;
  padding: 12px;
  background-color: transparent;
  border: none;
  border-bottom: 2px solid ${(p) => (p.active ? ACCENT : 'transparent')};
  color: ${(p) => (p.active ? '#9ef3d8' : '#6f8a8f')};
  font-weight: 600;
  font-size: 12px;
  letter-spacing: 0.08em;
  cursor: pointer;
  transition: all 0.2s;
  font-family: inherit;

  &:hover {
    color: ${ACCENT};
  }
`;

const ContentArea = styled.div`
  padding: 14px 12px 20px;
  font-family: inherit;
  min-height: calc(100vh - 98px);
`;

const InfoPanel = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
`;

const InfoContent = styled.div`
  flex: 1;
`;

const CompletedPermitsButton = styled.button`
  border: none;
  background: #1fe5b3;
  color: #06332c;
  border-radius: 999px;
  padding: 9px 12px;
  font-size: 12px;
  font-weight: 700;
  line-height: 1.1;
  cursor: pointer;
  margin-top: 18px;
  width: auto;
  align-self: center;

  &:hover {
    background: #54f2cd;
  }
`;

const AboutHeading = styled.div`
  color: #24d3a3;
  font-weight: 700;
  font-size: 12px;
  margin-bottom: 8px;
`;

const AboutBullet = styled.span`
  margin-right: 6px;
  color: #24d3a3;
`;

const AboutBody = styled.p`
  margin: 8px 0 0 0;
  color: #9db0b5;
  font-size: 12px;
  line-height: 1.45;
  font-weight: 400;
`;

const SectionTitle = styled.div`
  color: #24d3a3;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0;
  margin-bottom: 8px;
`;

const FeatureList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 16px 0;

  li {
    position: relative;
    padding-left: 0;
    margin-bottom: 6px;
    color: #8ea3a9;
    font-size: 12px;
    line-height: 1.55;
  }
  li::before {
    content: '•';
    margin-right: 4px;
    color: #8ea3a9;
  }
`;

const InstructionList = styled.ol`
  list-style: decimal;
  padding-left: 20px;
  margin: 0;

  li {
    margin-bottom: 4px;
    color: #9eb1b6;
    font-size: 12px;
    line-height: 1.55;
  }
`;

const HistoryItem = styled.div<{ active: boolean }>`
  padding: 12px;
  margin-bottom: 8px;
  background-color: rgba(15, 23, 42, 0.45);
  border: 1px solid ${(p) => (p.active ? 'rgba(52, 211, 153, 0.45)' : 'rgba(148, 163, 184, 0.25)')};
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: ${ACCENT};
  }
`;

const HistoryTitle = styled.div`
  color: ${TEXT_GRAY};
  font-weight: 600;
  font-size: 13px;
  margin-bottom: 4px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const HistoryMeta = styled.div`
  color: #546170;
  font-size: 12px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const LoadingText = styled.div`
  color: ${TEXT_GRAY};
  text-align: center;
  padding: 20px;
  font-size: 14px;
`;

const EmptyText = styled.div`
  color: ${TEXT_GRAY};
  text-align: center;
  padding: 20px;
  font-size: 14px;
`;

interface HistoryRecord {
  chat_id: string;
  title: string;
  user_id: string;
  status: 'in_progress' | 'completed';
  permit_metadata: {
    status: 'in_progress' | 'completed';
    created_at?: any;
    updated_at?: any;
    permit_file_urls?: {
      [key: string]: string;
    };
  };
}

interface SidebarProps {
  isHovered: boolean;
  setIsHovered: (hover: boolean) => void;
  onLoadChat?: (chatId: string) => void;
  activeChatId?: string | number | null;
  onStartNewChat?: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ onLoadChat, activeChatId, onStartNewChat }) => {
  const history = useHistory();
  const [activeTab, setActiveTab] = useState<'info' | 'history'>('info');
  const [historyRecords, setHistoryRecords] = useState<HistoryRecord[]>([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);

  useEffect(() => {
    if (activeTab === 'history') {
      fetchHistory();
    }
  }, [activeTab]);

  const fetchHistory = async () => {
    setIsLoadingHistory(true);
    try {
      const response: any = await permitAgentChat({
        type: 'list_chats',
        pageSize: 15,
      });

      const data = response && response.data && response.data.data ? response.data.data : [];
      setHistoryRecords(data as HistoryRecord[]);
    } catch (error) {
      // tslint:disable-next-line:no-console
      console.log('Error fetching history:', error);
      setHistoryRecords([]);
    } finally {
      setIsLoadingHistory(false);
    }
  };

  const handleHistoryItemClick = async (record: HistoryRecord) => {
    if (onLoadChat) {
      onLoadChat(record.chat_id);
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) {
      return '';
    }
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch {
      return String(dateString);
    }
  };

  const decodeTitle = (title: string) => {
    try {
      return decodeURIComponent(title.replace(/^PBR:\s*/, ''));
    } catch {
      return title.replace(/^PBR:\s*/, '');
    }
  };

  return (
    <PermitAgentSidebar>
      <SidebarHeader>
        <HeaderIcon
          className="bi bi-chevron-double-left"
          onClick={() => history.push('/search')}
          data-tip="Go back to search"
          data-for="back-to-search"
        />
        <HeaderIcon
          className="bi bi-plus-circle"
          onClick={() => {
            if (onStartNewChat) {
              onStartNewChat();
            }
            setActiveTab('info');
          }}
          data-tip="Start a new chat"
          data-for="start-new-chat"
        />
      </SidebarHeader>

      <TabRow>
        <TabButton
          active={activeTab === 'info'}
          onClick={() => setActiveTab('info')}
        >
          INFO
        </TabButton>
        <TabButton
          active={activeTab === 'history'}
          onClick={() => setActiveTab('history')}
        >
          HISTORY
        </TabButton>
      </TabRow>

      <ContentArea>
        {activeTab === 'info' ? (
          <InfoPanel>
            <InfoContent>
              <AboutHeading>
                <AboutBullet>•</AboutBullet>
                About
              </AboutHeading>
              <AboutBody>
                Analyzes and prepares environmental permits and authorizations.
              </AboutBody>

              <SectionTitle>Features:</SectionTitle>
              <FeatureList>
                <li>Compliance analysis</li>
                <li>Authorization ID</li>
                <li>Form data extraction</li>
                <li>Engineering prep</li>
                <li>PDF form filling</li>
                <li>Document download</li>
              </FeatureList>

              <SectionTitle>Instructions</SectionTitle>
              <InstructionList>
                <li>Enter what you want to permit</li>
                <li>Agent analyzes compliance and finds authorization routes</li>
                <li>Agent prepares the permit package</li>
                <li>Download to submit to the agency</li>
              </InstructionList>
            </InfoContent>
            <CompletedPermitsButton type="button" onClick={() => setActiveTab('history')}>
              Completed Permits
            </CompletedPermitsButton>
          </InfoPanel>
        ) : (
          <div>
            {isLoadingHistory ? (
              <LoadingText>Loading history...</LoadingText>
            ) : historyRecords.length === 0 ? (
              <EmptyText>No chat history found.</EmptyText>
            ) : (
              historyRecords.map((record) => (
                <HistoryItem
                  key={record.chat_id}
                  active={activeChatId?.toString() === record.chat_id.toString()}
                  onClick={() => handleHistoryItemClick(record)}
                >
                  <HistoryTitle>{decodeTitle(record.title)}</HistoryTitle>
                  <HistoryMeta>
                    <span>{record.status}</span>
                    <span>{formatDate(record.permit_metadata && record.permit_metadata.created_at)}</span>
                  </HistoryMeta>
                </HistoryItem>
              ))
            )}
          </div>
        )}
      </ContentArea>
      <StyledTooltip id="back-to-search" effect="solid" place="right" />
      <StyledTooltip id="start-new-chat" effect="solid" place="right" />
    </PermitAgentSidebar>
  );
};

export default Sidebar;
